import React, { useState, useEffect } from "react";
import {
  Button,
  Modal,
  TextField,
  Typography,
  Box,
  Select,
  MenuItem,
  IconButton,
  Grid,
  Card,
  CardContent,
  CardActions,
  useTheme,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import {
  fetchMasterAccounts,
  createSubaccount,
  fetchSubaccounts,
  updateSubaccount,
  deleteSubaccount,
} from "../api/customerApi";

interface MasterAccount {
  id: number;
  name: string;
  allow_subaccount_creation: boolean;
}

interface Subaccount {
  id: number;
  name: string;
  email: string;
  phone: string;
  shipping_address: string;
}

const SubaccountManager = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [masterAccounts, setMasterAccounts] = useState<MasterAccount[]>([]);
  const [selectedMasterAccount, setSelectedMasterAccount] = useState<number | null>(null);
  const [subaccounts, setSubaccounts] = useState<Subaccount[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingSubaccountId, setEditingSubaccountId] = useState<number | null>(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    shipping_address: "",
    password: "",
  });

  const selectedMaster = masterAccounts.find(m => m.id === selectedMasterAccount);
  const canAddSubaccounts = selectedMaster?.allow_subaccount_creation ?? false;

  useEffect(() => {
    fetchMasters();
  }, []);

  const fetchMasters = async () => {
    try {
      const data = await fetchMasterAccounts();
      setMasterAccounts(data);
    } catch (error) {
      console.error("Error fetching master accounts:", error);
    }
  };

  const fetchSubaccountsList = async (masterCustomerId: number) => {
    try {
      const data = await fetchSubaccounts(masterCustomerId);
      setSubaccounts(data);
    } catch (error) {
      console.error("Error fetching subaccounts:", error);
    }
  };

  const handleSaveSubaccount = async () => {
    try {
      if (isEditing && editingSubaccountId) {
        await updateSubaccount(editingSubaccountId, {
          name: form.name,
          email: form.email,
          phone: form.phone,
          shippingAddress: form.shipping_address,
          password: form.password || undefined,
        });
      } else {
        if (selectedMasterAccount === null) {
          alert("Please select a master account before adding a subaccount.");
          return;
        }
        await createSubaccount({
          masterCustomerId: selectedMasterAccount,
          name: form.name,
          email: form.email,
          phone: form.phone,
          shippingAddress: form.shipping_address,
          password: form.password || undefined,
        });
      }
      resetForm();
      fetchSubaccountsList(selectedMasterAccount!);
    } catch (error) {
      console.error("Error saving subaccount:", error);
    }
  };

  const handleDeleteSubaccount = async (subaccountId: number) => {
    try {
      await deleteSubaccount(subaccountId);
      fetchSubaccountsList(selectedMasterAccount!);
    } catch (error) {
      console.error("Error deleting subaccount:", error);
    }
  };

  const openEditModal = (subaccount: Subaccount) => {
    setForm({
      name: subaccount.name,
      email: subaccount.email,
      phone: subaccount.phone,
      shipping_address: subaccount.shipping_address,
      password: "",
    });
    setEditingSubaccountId(subaccount.id);
    setIsEditing(true);
    setOpenModal(true);
  };

  const resetForm = () => {
    setForm({ name: "", email: "", phone: "", shipping_address: "", password: "" });
    setEditingSubaccountId(null);
    setIsEditing(false);
    setOpenModal(false);
  };

  return (
    <Box sx={{ padding: 3 }}>
      <Typography variant="h5" sx={{ marginBottom: 3, fontWeight: "bold" }}>
        Manage Subaccounts
      </Typography>

      {/* Master Account Selector */}
      <Select
        value={selectedMasterAccount || ""}
        onChange={(e) => {
          const masterId = Number(e.target.value);
          setSelectedMasterAccount(masterId);
          fetchSubaccountsList(masterId);
        }}
        fullWidth
        displayEmpty
        sx={{
          mb: 3,
          bgcolor: theme.palette.background.paper,
          color: theme.palette.text.primary,
        }}
      >
        <MenuItem value="" disabled>
          Select a Master Account
        </MenuItem>
        {masterAccounts.map((master) => (
          <MenuItem key={master.id} value={master.id}>
            {master.name} {!master.allow_subaccount_creation && "(Subaccounts disabled)"}
          </MenuItem>
        ))}
      </Select>

      {selectedMasterAccount && (
        <>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
            <Typography variant="h6">Subaccounts</Typography>
            {canAddSubaccounts ? (
              <Button
                startIcon={<AddIcon />}
                variant="contained"
                onClick={() => {
                  resetForm();
                  setIsEditing(false);
                  setOpenModal(true);
                }}
              >
                Add Subaccount
              </Button>
            ) : (
              <Tooltip title="Contact administrator to enable subaccount creation">
                <Typography variant="body2" color="error" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>⚠️ Subaccount creation is disabled for this master account</span>
                </Typography>
              </Tooltip>
            )}
          </Box>

          {/* Subaccount List */}
          <Grid container spacing={2}>
            {subaccounts.map((sub) => (
              <Grid size={{xs: 12, sm: 6, md: 4}} key={sub.id}>
                <Card>
                  <CardContent>
                    <Typography variant="h6">{sub.name}</Typography>
                    <Typography variant="body2">Email: {sub.email}</Typography>
                    <Typography variant="body2">Phone: {sub.phone}</Typography>
                    <Typography variant="body2">Address: {sub.shipping_address}</Typography>
                  </CardContent>
                  <CardActions>
                    <IconButton
                      onClick={() => openEditModal(sub)}
                      sx={{ color: theme.palette.primary.main }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDeleteSubaccount(sub.id)}
                      sx={{ color: theme.palette.error.main }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* Modal for Adding/Editing Subaccount */}
      <Modal open={openModal} onClose={resetForm}>
        <Box
          sx={{
            background: theme.palette.background.default,
            color: theme.palette.text.primary,
            padding: 4,
            margin: "auto",
            marginTop: "10%",
            borderRadius: 4,
            width: "90%",
            maxWidth: "400px",
            boxShadow: 4,
          }}
        >
          <Typography variant="h6" sx={{ marginBottom: 2 }}>
            {isEditing ? "Edit Subaccount" : "Add Subaccount"}
          </Typography>
          <TextField
            label="Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            fullWidth
            margin="normal"
            type="email"
            required
          />
          <TextField
            label="Phone"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Shipping Address"
            value={form.shipping_address}
            onChange={(e) => setForm({ ...form, shipping_address: e.target.value })}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Password (Optional)"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            fullWidth
            margin="normal"
            type="password"
            helperText={isEditing ? "Leave blank to keep existing password" : "Default password will be generated if left blank"}
          />
          <Button
            variant="contained"
            onClick={handleSaveSubaccount}
            fullWidth
            sx={{ mt: 3 }}
            disabled={!form.name || !form.email || !form.phone || !form.shipping_address}
          >
            {isEditing ? "Update Subaccount" : "Create Subaccount"}
          </Button>
        </Box>
      </Modal>
    </Box>
  );
};

export default SubaccountManager;